/**
 * Dashboard Health Calculation Utilities
 *
 * Computes Traction-style health indicators (traffic lights) from report data
 */

export type HealthStatus = 'healthy' | 'warning' | 'critical';

export interface HealthIndicator {
  label: string;
  status: HealthStatus;
  value: string;
  description: string;
  link?: string; // Navigation link for drilling down
}

/**
 * Calculate cash health based on 90-day net forecast
 */
export function calculateCashHealth(netForecast: number): HealthIndicator {
  let status: HealthStatus;
  let description: string;

  if (netForecast >= 50000) {
    status = 'healthy';
    description = 'Strong cash position';
  } else if (netForecast >= 10000) {
    status = 'warning';
    description = 'Monitor cash closely';
  } else {
    status = 'critical';
    description = 'Action needed';
  }

  return {
    label: 'Cash',
    status,
    value: formatCurrency(netForecast),
    description,
  };
}

/**
 * Calculate collections health based on overdue invoices
 */
export function calculateCollectionsHealth(
  totalOutstanding: number,
  totalOverdue: number
): HealthIndicator {
  const overduePercentage = totalOutstanding > 0
    ? (totalOverdue / totalOutstanding) * 100
    : 0;

  let status: HealthStatus;
  let description: string;

  if (overduePercentage < 10) {
    status = 'healthy';
    description = 'On track';
  } else if (overduePercentage < 30) {
    status = 'warning';
    description = `${overduePercentage.toFixed(0)}% overdue`;
  } else {
    status = 'critical';
    description = 'Collections issue';
  }

  return {
    label: 'Collections',
    status,
    value: `${overduePercentage.toFixed(0)}%`,
    description,
    link: '/reports?tab=outstanding-invoices',
  };
}

/**
 * Calculate project health based on at-risk/over-budget projects
 */
export function calculateProjectHealth(
  healthyCount: number,
  atRiskCount: number,
  overBudgetCount: number
): HealthIndicator {
  const totalProjects = healthyCount + atRiskCount + overBudgetCount;
  const problematicCount = atRiskCount + overBudgetCount;
  const problematicPercentage = totalProjects > 0
    ? (problematicCount / totalProjects) * 100
    : 0;

  let status: HealthStatus;
  let description: string;

  if (problematicPercentage < 20) {
    status = 'healthy';
    description = 'Projects on track';
  } else if (problematicPercentage < 40) {
    status = 'warning';
    description = `${problematicCount} need attention`;
  } else {
    status = 'critical';
    description = 'Review budgets';
  }

  return {
    label: 'Projects',
    status,
    value: `${healthyCount}/${totalProjects}`,
    description,
  };
}

/**
 * Calculate pipeline health based on stale proposals
 */
export function calculatePipelineHealth(
  activeValue: number,
  agingValue: number,
  staleValue: number
): HealthIndicator {
  const totalPipeline = activeValue + agingValue + staleValue;
  const stalePercentage = totalPipeline > 0
    ? (staleValue / totalPipeline) * 100
    : 0;

  let status: HealthStatus;
  let description: string;

  if (stalePercentage < 30) {
    status = 'healthy';
    description = 'Pipeline fresh';
  } else if (stalePercentage < 50) {
    status = 'warning';
    description = 'Follow up on proposals';
  } else {
    status = 'critical';
    description = 'Stale proposals';
  }

  return {
    label: 'Pipeline',
    status,
    value: formatCurrency(totalPipeline),
    description,
    link: '/reports?tab=revenue-pipeline',
  };
}

/**
 * Calculate pipeline health for dashboard (Active proposals only)
 * Health based on proposal count and value
 */
export function calculateActivePipelineHealth(
  activeValue: number,
  activeCount: number,
  regularValue: number,
  optionalValue: number
): HealthIndicator & { regularValue: number; optionalValue: number } {
  let status: HealthStatus;
  let description: string;

  // Health based on having active proposals
  if (activeCount >= 3 && activeValue >= 50000) {
    status = 'healthy';
    description = 'Strong pipeline';
  } else if (activeCount >= 1 || activeValue > 0) {
    status = 'warning';
    description = 'Build pipeline';
  } else {
    status = 'critical';
    description = 'No active proposals';
  }

  return {
    label: 'Pipeline',
    status,
    value: formatCurrency(activeValue),
    description: `Active proposals (<30 days)`,
    link: '/reports?tab=revenue-pipeline',
    regularValue,
    optionalValue,
  };
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}k`;
  } else {
    return `$${amount.toFixed(0)}`;
  }
}

/**
 * Get CSS classes for health status
 */
export function getHealthStatusClasses(status: HealthStatus): {
  bg: string;
  text: string;
  border: string;
  icon: string;
} {
  switch (status) {
    case 'healthy':
      return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        icon: 'text-emerald-500',
      };
    case 'warning':
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        icon: 'text-amber-500',
      };
    case 'critical':
      return {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-200',
        icon: 'text-rose-500',
      };
  }
}

/**
 * Get icon name for health status (used by HealthIndicators component)
 */
export type HealthIconName = 'check-circle' | 'alert-circle' | 'x-circle';

export function getHealthStatusIcon(status: HealthStatus): HealthIconName {
  switch (status) {
    case 'healthy':
      return 'check-circle';
    case 'warning':
      return 'alert-circle';
    case 'critical':
      return 'x-circle';
  }
}
